import { useState, useEffect } from 'react';
import { GameSession, PlayerWithUsername } from '../types';
import apiService from '../services/api';
import useAuthStore from '../stores/authStore';

export interface ActiveAdventure {
  session: GameSession;
  characterName?: string;
  currentSceneDescription?: string;
  playerId?: string;
}

export interface UseActiveAdventuresResult {
  adventures: ActiveAdventure[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useActiveAdventures = (): UseActiveAdventuresResult => {
  const [adventures, setAdventures] = useState<ActiveAdventure[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchActiveAdventures = async () => {
    // Check if user is authenticated first (and not a guest)
    const authState = useAuthStore.getState();
    const currentUser = authState.user;
    
    // Guest users don't have active adventures - they're just starting
    // Skip API calls for guest users to avoid "Access token is required" errors
    if (!currentUser || authState.isGuest) {
      setAdventures([]);
      setLoading(false);
      setError(null);
      return;
    }
    try {
      setLoading(true);
      setError(null);

      // Get active sessions from the backend
      const sessions = await apiService.getActiveSessions();
      
      // Helper function to extract character name
      const getCharacterName = (player: PlayerWithUsername) => {
        return player.characterName && player.characterName.trim() !== '' 
          ? player.characterName 
          : null;
      };

      // For each session, get the current user's player info and current scene
      const adventuresWithDetails = await Promise.all(
        sessions.map(async (session) => {
          try {
            
            // Get players in this session
            const players = await apiService.getSessionPlayers(session.id);
            
            // Find current user's player
            const currentUser = useAuthStore.getState().user;
            
            const currentUserPlayer = players.find(player => 
              (player as PlayerWithUsername).userId === currentUser?.id
            );
            
            if (!currentUserPlayer) {
              return null; // User is not a player in this session
            }

            try {
              // Get current scene for this player
              const currentScene = await apiService.getCurrentScene(session.id, currentUserPlayer.id);
              
              // Get description from first section if available, otherwise use scene ID
              const description = currentScene.scene?.sections?.[0]?.description 
                ? (typeof currentScene.scene.sections[0].description === 'string' 
                    ? currentScene.scene.sections[0].description 
                    : 'Scene description')
                : `Scene: ${currentScene.scene?.id || 'Unknown'}`;
              
              return {
                session,
                characterName: getCharacterName(currentUserPlayer),
                currentSceneDescription: description,
                playerId: currentUserPlayer.id
              };
            } catch (sceneError) {
              // If we can't get scene details, still include the adventure
              console.warn(`useActiveAdventures: Failed to load scene details for session ${session.id}:`, sceneError);
              return {
                session,
                characterName: getCharacterName(currentUserPlayer),
                currentSceneDescription: 'Continue your adventure...',
                playerId: currentUserPlayer.id
              };
            }
          } catch (error) {
            console.error(`useActiveAdventures: Error processing session ${session.id}:`, error);
            return null; // Skip this session if there's an error
          }
        })
      );

      // Filter out null entries and cast to ActiveAdventure[]
      const validAdventures = adventuresWithDetails.filter((adventure) => 
        adventure !== null
      ) as ActiveAdventure[];

      setAdventures(validAdventures);
    } catch (err) {
      console.error('useActiveAdventures: Error occurred:', err);
      
      // Show errors for API issues, but gracefully handle missing endpoint
      const errorMessage = err instanceof Error ? err.message : 'Failed to load active adventures';
      
      // If it's a 404 or the endpoint doesn't exist, just return empty array
      if (errorMessage.includes('404') || errorMessage.includes('not found')) {
        setAdventures([]);
        setError(null); // Don't show error to user for missing backend feature
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActiveAdventures();
  }, []);

  return {
    adventures,
    loading,
    error,
    refetch: fetchActiveAdventures
  };
};